// Holdings domain helpers — pure functions, no React, no i18n (callers do the
// `t()`), so they can be reasoned about and reused by any consumer.
//
// The Dashboard card needs ONE normalized list that renders models-only,
// funds-only and mixed portfolios identically (FM-1927). That list is a join of
// two payloads the page already fetches:
//
//   /dashboard  holdings.funds[]  — the FLAT list: { name, ticker, pct, amount, ytd }
//   /portfolio  benchmarkMeta.models[] — { modelId, name, pct, balance, funds: [ticker] }
//
// The dashboard list is flat and ALREADY CONTAINS the model's member funds —
// verified against ACC-P001-AXOS, where VFFVX 40% + VTSAX 20% + VTIAX 10% = 70%,
// exactly the model's own `pct`. So a model is a GROUPING over funds already
// present, never an extra item: emitting both would double-count the account.
import { CATEGORICAL, colorAt } from './chartColors';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** A share of `whole`, as whole percent — the rescaling models use for children. */
const share = (part, whole) => (whole ? Math.round((num(part) / whole) * 100) : 0);

/**
 * Whole-number shares of `values` that add to exactly `target` (FM-3056/FM-3084), as Classic
 * prints allocations. Each share is computed from the values and rounded down; the points
 * still missing go one each to the lines that lost the most to rounding (ties to the larger
 * line), so no line is more than 1 off its exact share. Handing the whole leftover to the
 * largest line instead put a 14.66% fund at 12% on a 20-fund model.
 */
export const wholePercents = (values = [], target = 100) => {
  const vs = values.map((v) => Math.max(0, num(v)));
  const sum = vs.reduce((s, v) => s + v, 0);
  if (!sum) return vs.map(() => 0);
  const exact = vs.map((v) => (v / sum) * target);
  const out = exact.map(Math.floor);
  const left = Math.round(target) - out.reduce((s, v) => s + v, 0);
  exact
    .map((e, i) => [e - out[i], i])
    .sort((a, b) => b[0] - a[0] || vs[b[1]] - vs[a[1]])
    .slice(0, left)
    .forEach(([, i]) => { out[i] += 1; });
  return out;
};

/**
 * Share of FUTURE contributions directed at a ticker, or NULL when there is no
 * election on record for it.
 *
 * `/dashboard` carries no election — its funds are { name, ticker, pct, amount, ytd }.
 * The figure comes from the portfolio slice the Dashboard page already fetches for
 * models and fund colours, keyed by ticker (the same seam as `colorByTicker`), so the
 * FUTURE column costs no extra request. Null is NOT 0: a holding with no election on
 * record has not been elected at 0%, and the row says so with an em dash.
 */
const electionFor = (ticker, map) => {
  const v = map?.[ticker];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/**
 * A participant loan is NEVER a holding. It rides in on the same list because the
 * recordkeeper treats it as a position, but it is money owed, not money invested —
 * counting it inflates the allocation bar, the "N funds" count and the account
 * total. Filtered out of the item list and therefore out of every derivation
 * downstream (widths, legend, counts).
 */
const isLoan = (f) => f?.type === 'loan';

/**
 * Colour for one holding. Cross-screen identity wins: a ticker the portfolio slice
 * knows keeps ITS colour, so a fund reads the same on the Dashboard, the Portfolio page
 * and in the fund modal. `CATEGORICAL` (from `utils/chartColors`, the one owner of these
 * sequences) colours everything else by position — in practice that means MODELS, which
 * have no `tkr`, and tickers the portfolio slice hasn't loaded.
 *
 * KNOWN CONSEQUENCE, deliberately not "fixed" here: the portfolio slice colours funds
 * from `FUND_BLUE_RAMP` by balance rank (`portfolio.adapters.js:373`), so on the
 * Dashboard allocation bar the two largest fund segments take the two darkest ramp
 * entries and sit adjacent as near-identical navy. `CATEGORICAL` is therefore effectively
 * unreachable for real funds, and it cannot resolve that overlap while identity wins.
 *
 * The design disagrees with the current priority: its dashboard holdings are multi-hue
 * per fund (`dashboard-redesign.jsx:2985-2991` — VFFVX #0077CC, VTSAX #7BB9E5,
 * VTIAX #552299, VTIP #119944), and it reserves a monochromatic ramp for the SOURCES bar,
 * where magnitude is the message. Switching the Dashboard bar to `CATEGORICAL` is a
 * one-line change here — it trades cross-screen fund identity for in-screen segment
 * legibility. That trade is a product call, so it is named rather than made.
 */
export const holdingColor = (item, index, colorByTicker = {}) =>
  (item.tkr && colorByTicker[item.tkr]) || colorAt(CATEGORICAL, index);

/**
 * The normalized item list, sorted large → small so the allocation bar reads
 * high-to-low and the legend's "N more" collapses the genuinely smallest.
 *
 * @param {object} o
 * @param {Array} o.funds   `/dashboard` holdings.funds
 * @param {Array} o.models  `/portfolio` benchmarkMeta.models
 * @param {object} [o.colorByTicker]  ticker → colour, from the portfolio slice
 * @param {object} [o.electionByTicker]  ticker → future-contribution %, same slice
 * @returns {Array<{id,type,name,tkr,balancePct,balanceAmt,electionPct,ytd,color,children}>}
 */
export const buildHoldingItems = ({
  funds = [], models = [], colorByTicker = {}, electionByTicker = {},
} = {}) => {
  const list = (Array.isArray(funds) ? funds : []).filter((f) => !isLoan(f));
  const mdls = Array.isArray(models) ? models : [];

  // Every ticker claimed by a model, so the remainder are the standalone funds.
  const claimed = new Set(mdls.flatMap((m) => m.funds || []));

  const modelItems = mdls
    .map((m) => {
      const members = list.filter((f) => (m.funds || []).includes(f.ticker));
      if (!members.length) return null; // a model whose funds aren't in this payload
      const pct = num(m.pct) || members.reduce((s, f) => s + num(f.pct), 0);
      // Contributions are directed AT the model, so its election is what its members
      // sum to. Null unless at least one member HAS an election — summing a set of
      // unknowns to 0 would report "0% of future contributions" for a model that may
      // well be receiving all of them.
      const memberElections = members.map((f) => electionFor(f.ticker, electionByTicker));
      const known = memberElections.filter((v) => v != null);
      const election = known.length
        ? Math.round(known.reduce((s, v) => s + v, 0) * 10) / 10
        : null;
      return {
        id: m.modelId,
        type: 'model',
        name: m.name,
        tkr: null,
        balancePct: pct,
        balanceAmt: num(m.balance) || members.reduce((s, f) => s + num(f.amount), 0),
        electionPct: election,
        // Models carry no return of their own on the wire — the row renders "—".
        ytd: null,
        children: members.map((f, ci) => ({
          name: f.name,
          ticker: f.ticker,
          // Rescaled to a share WITHIN the model: VFFVX is 40% of the account but
          // 57% of a 70% model. Showing the account-level figure inside an expanded
          // model would read as though the children summed to 70, not 100.
          balancePct: share(f.pct, pct),
          balanceAmt: num(f.amount),
          // Rescaled the same way, for the same reason — it sits in the column
          // beside `balancePct`, so the two must be shares of the same whole.
          electionPct: memberElections[ci] == null || !election
            ? null
            : share(memberElections[ci], election),
          ytd: typeof f.ytd === 'number' ? f.ytd : null,
        })),
      };
    })
    .filter(Boolean);

  const standalone = list
    .filter((f) => !claimed.has(f.ticker))
    .map((f) => ({
      id: f.ticker,
      type: 'fund',
      name: f.name,
      tkr: f.ticker,
      balancePct: num(f.pct),
      balanceAmt: num(f.amount),
      electionPct: electionFor(f.ticker, electionByTicker),
      ytd: typeof f.ytd === 'number' ? f.ytd : null,
      children: [],
    }));

  // Colour is assigned AFTER sorting so the ramp runs largest → smallest and the
  // biggest holdings get the most distinct colours.
  return [...modelItems, ...standalone]
    .sort((a, b) => b.balancePct - a.balancePct)
    .map((item, i) => ({ ...item, color: holdingColor(item, i, colorByTicker) }));
};

/**
 * "1 model · 2 funds" — derived from the item types, never hardcoded. Returns the
 * COUNTS; the caller supplies the words so this stays i18n-free.
 * @returns {{models: number, funds: number}}
 */
export const holdingCounts = (items = []) => ({
  models: items.filter((i) => i.type === 'model').length,
  funds: items.filter((i) => i.type === 'fund').length,
});

/** Segment width as a share of the total, so segments always sum to ~100%. */
export const segmentWidths = (items = []) => {
  const sum = items.reduce((s, i) => s + num(i.balancePct), 0) || 1;
  return items.map((i) => (num(i.balancePct) / sum) * 100);
};

/**
 * Legend chips, capped so the row stays readable: the largest `max - 1` holdings
 * keep their own chip and everything smaller collapses into one "N more" chip
 * carrying the summed percentage.
 *
 * @returns {Array<{item}|{more: true, count, pct, ids}>}
 */
export const legendChips = (items = [], max = 5) => {
  if (items.length <= max) return items.map((item) => ({ item }));
  const shown = items.slice(0, max - 1);
  const rest = items.slice(max - 1);
  return [
    ...shown.map((item) => ({ item })),
    {
      more: true,
      count: rest.length,
      pct: Math.round(rest.reduce((s, i) => s + num(i.balancePct), 0)),
      ids: rest.map((i) => i.id),
    },
  ];
};

/**
 * Legible label colour for an in-segment percentage, by relative luminance
 * (ITU-R BT.601). Segment colours span pale yellows to deep navies, so a single
 * fixed colour is unreadable on one end or the other.
 */
export const segmentTextColor = (hex) => {
  const c = String(hex || '#000').replace('#', '');
  if (c.length < 6) return '#fff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? 'rgba(20,22,34,0.82)' : '#fff';
};
